// S5b.3: WHO A FLOW IS, in one place.
//
// A tab opened by /launch holds a launch (its state in session storage) and a
// launch user on the launch app's own Auth (session persistence, S5b.2). While
// both are there, every flow reads and writes as that user, through the launch
// app's Auth, Firestore and Functions, and every save is attributed from the
// launch, never from a ?code=. Anything else, including a launched tab whose
// launch user is gone (expired, signed out), is today's path on the default
// instance. Never a half state, and nothing here ever changes the default
// instance's persistence (the V9 control showed that signs other tabs out).
//
// The flows do not branch on this themselves: AuthContext and the data modules
// (workStyleData, motivatedSkillsData, assessmentData) ask it.
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, functions } from './firebase';
import { getLaunchAuth, hasLaunchState, launchFirestore, launchFunctions, readLaunchState } from './launch';

/** The launch identity, or null. Synchronous: the launch user as the launch Auth holds it now. */
export function launchIdentity() {
  if (!hasLaunchState()) return null;
  const launchAuth = getLaunchAuth();
  if (!launchAuth.currentUser) return null;
  return { mode: 'launch', auth: launchAuth, db: launchFirestore(), functions: launchFunctions(), user: launchAuth.currentUser, launch: readLaunchState() };
}

/** Which Auth, Firestore and Functions a flow uses right now. */
export function activeIdentity() {
  return launchIdentity() || { mode: 'default', auth, db, functions, user: auth ? auth.currentUser : null, launch: null };
}

/**
 * The user a flow sees, as it changes: the launch user while there is one, else
 * the default instance's. Fires once both instances have answered.
 */
export function onIdentityChanged(cb) {
  const launched = hasLaunchState();
  let launchUser = null;
  let defaultUser = null;
  let launchReady = !launched;
  let defaultReady = false;
  const emit = () => {
    if (!launchReady || !defaultReady) return;
    cb(launchUser ? { mode: 'launch', user: launchUser } : { mode: 'default', user: defaultUser });
  };
  const unsubs = [
    onAuthStateChanged(auth, (u) => {
      defaultUser = u;
      defaultReady = true;
      emit();
    }),
  ];
  if (launched) {
    unsubs.push(
      onAuthStateChanged(getLaunchAuth(), (u) => {
        launchUser = u;
        launchReady = true;
        emit();
      }),
    );
  }
  return () => unsubs.forEach((u) => u());
}

/*
 * S5b.5: RESUME BY ATTEMPT. A retake is a new attempt of the same grant, and it
 * starts fresh: it never resumes into an earlier attempt's answers. Progress
 * saved under a launch carries the grant and attempt it belongs to; under a
 * launch, only progress for the same grant and attempt resumes. With no
 * launch, nothing changes.
 */

/** The grant and attempt this tab's progress belongs to, or null with no launch. */
export function launchAttemptTag() {
  const identity = launchIdentity();
  const launch = identity && identity.launch;
  return launch ? { grantId: launch.grantId, attempt: launch.attempt } : null;
}

const sameAttempt = (a, b) => !!a && !!b && a.grantId === b.grantId && a.attempt === b.attempt;

/** Whether an account-level progress record may resume here. */
export function progressFitsLaunch(record) {
  const tag = launchAttemptTag();
  return !tag || sameAttempt(record && record.launch, tag);
}

/** The progress record to store: the state, tagged with its attempt under a launch. */
export function tagProgress(record) {
  const tag = launchAttemptTag();
  return tag ? { ...record, launch: tag } : record;
}

// A flow's device copy lives in local storage, which every tab and attempt
// shares, so its attempt is kept beside it under a companion key.
const deviceTagKey = (storageKey) => `${storageKey}::launch`;

/** Whether the device copy under `storageKey` may resume here. */
export function deviceProgressFits(storageKey) {
  const tag = launchAttemptTag();
  if (!tag) return true;
  try {
    return sameAttempt(JSON.parse(window.localStorage.getItem(deviceTagKey(storageKey)) || 'null'), tag);
  } catch {
    return false;
  }
}

/** Record that the device copy under `storageKey` belongs to this launch's attempt. */
export function markDeviceProgress(storageKey) {
  const tag = launchAttemptTag();
  try {
    if (tag) window.localStorage.setItem(deviceTagKey(storageKey), JSON.stringify(tag));
    else window.localStorage.removeItem(deviceTagKey(storageKey));
  } catch {
    // Best effort; a missing tag only means a launch starts fresh.
  }
}

export class LaunchMismatchError extends Error {
  constructor(message) {
    super(message);
    this.name = 'LaunchMismatchError';
    this.code = 'LAUNCH_STATE_MISMATCH';
  }
}

const FIELDS = ['grantId', 'clientId', 'coachId', 'attempt'];

/**
 * A launched save's attribution, or null when this is not a launch.
 *
 * The token's `apcLaunch` claim is the source of truth and the tab's session
 * state is its cache. Where they disagree on any of the four fields the save
 * refuses, rather than picking one.
 */
export async function launchAttribution() {
  const identity = launchIdentity();
  if (!identity) return null;
  const { claims } = await identity.user.getIdTokenResult();
  const claim = claims && claims.apcLaunch;
  const state = identity.launch || {};
  if (!claim || FIELDS.some((f) => (claim[f] ?? null) !== (state[f] ?? null))) {
    throw new LaunchMismatchError('This tab\'s launch does not match its sign-in. Go back to your journey and open it again.');
  }
  return {
    coachId: claim.coachId ?? null,
    clientId: claim.clientId,
    grantId: claim.grantId,
    attempt: claim.attempt,
    communityCode: null,
    cohort: null,
    source: 'launch',
    legacyCode: null,
  };
}
